import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, ShieldCheck, ArrowLeft, Award, Save, ClipboardCheck, FileDown, Scale, Undo2, History, X, Sparkles } from "lucide-react";
import { CierreAttachments } from "../components/pg07/CierreAttachments";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADO_META = {
  Done: { label: "Done", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  WIP: { label: "WIP", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  NotS: { label: "NotS", cls: "bg-red-100 text-red-700 border-red-300" },
  NA: { label: "NA", cls: "bg-slate-100 text-slate-500 border-slate-300" },
};

/**
 * Auren per-Project + Ficha Cierre.
 * URL: /pg07/extras?project=<id>
 */
export default function PG07ExtraFichasPage() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState(() => new URLSearchParams(window.location.search).get("project") || "");
  const [globalSummary, setGlobalSummary] = useState(null);

  useEffect(() => {
    fetch(`${API}/pg07/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setProjects).catch(() => {});
    fetch(`${API}/pg07/auren/global-summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setGlobalSummary).catch(() => {});
  }, [token]);

  // update URL when pid changes
  useEffect(() => {
    if (pid) {
      window.history.replaceState({}, "", `${window.location.pathname}?project=${pid}`);
    }
  }, [pid]);

  return (
    <div className="space-y-6" data-testid="pg07-extra-fichas-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button className="text-xs text-emerald-700 hover:underline flex items-center gap-1 mb-1" onClick={() => navigate("/pg07")} data-testid="back-to-pg07">
            <ArrowLeft className="w-3 h-3" /> Volver a PG-07
          </button>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Sistema de Gestión I+D+i 56001</div>
          <h1 className="text-3xl font-black flex items-center gap-2"><ClipboardCheck className="w-7 h-7 text-emerald-600" /> Fichas ISO 56001 por proyecto & Cierre</h1>
        </div>
        <div className="w-72">
          <Label>Selecciona proyecto</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="project-selector"><SelectValue placeholder="Escoge un proyecto..." /></SelectTrigger>
            <SelectContent>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!pid ? (
        <>
          {globalSummary?.rows?.length > 0 && (
            <Card data-testid="auren-global-summary">
              <CardContent className="p-4">
                <h3 className="text-sm font-bold flex items-center gap-2 mb-1"><Award className="w-4 h-4" />Ficha ISO 56001 por proyecto · Ranking global</h3>
                <p className="text-[10px] text-muted-foreground mb-3">
                  Los proyectos marcados "hereda global" aún no tienen overrides propios — su % refleja el checklist maestro ({globalSummary.rows[0]?.total_items ?? "94"} items), no una revisión específica de ese proyecto.
                </p>
                <div className="space-y-1.5 max-h-96 overflow-auto">
                  {globalSummary.rows.map((r, i) => (
                    <button key={r.project_id} onClick={() => setPid(r.project_id)} className="w-full text-left flex items-center gap-3 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`auren-summary-row-${i}`}>
                      <span className="w-6 text-xs text-muted-foreground">{i + 1}</span>
                      <span className="flex-1 truncate text-sm">{r.project_name}</span>
                      {!r.has_overrides && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-muted-foreground" title="Este proyecto aún no tiene overrides propios: el % mostrado es el checklist global heredado, no una auditoría real de este proyecto." data-testid={`auren-summary-inherited-${i}`}>
                        hereda global
                        </span>
                      )}
                      <Badge variant="outline" className="text-[10px]">{r.applies_count} aplic.</Badge>
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700">{r.done} done</Badge>
                      <span className="font-mono text-sm w-14 text-right">{r.pct_done}%</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <>
          <AurenProjectSection projectId={pid} projectName={(projects.find(p => p.id === pid)?.name) || pid} token={token} estadoMeta={ESTADO_META} />
          <AuditoriaSection projectId={pid} token={token} />
          <CierreSection projectId={pid} token={token} />
          <CierreAttachments projectId={pid} token={token} />
        </>
      )}
    </div>
  );
}

function AurenProjectSection({ projectId, projectName, token, estadoMeta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});  // {item_num: override}
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState({});
  const [bulkFilter, setBulkFilter] = useState("all");
  const [undoStack, setUndoStack] = useState([]); // max 5 snapshots [{dirty, selected, action, label, ts}]
  const [dayLog, setDayLog] = useState(() => {
    try {
      const raw = localStorage.getItem("pg07_timeline_v1");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineFilters, setTimelineFilters] = useState({ project: "", action: "", onlyReverted: false });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null); // {count, suggestions:[]}
  const [applying, setApplying] = useState(false);

  const aiInfer = async () => {
    setAiLoading(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${projectId}/auren/ai-infer`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Error IA");
      }
      const d = await r.json();
      setAiSuggestions(d);
      toast.success(`🤖 IA propuso ${d.count} sugerencias — revísalas y aplica`);
    } catch (e) { toast.error(e.message || "Error IA"); }
    finally { setAiLoading(false); }
  };

  const applySuggestions = async (onlyNew) => {
    if (!aiSuggestions?.suggestions?.length) return;
    if (!onlyNew && !window.confirm("¿Sobrescribir TODOS los items del checklist con las sugerencias IA? Podrás deshacer los cambios locales.")) return;
    setApplying(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${projectId}/auren/apply-suggestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ suggestions: aiSuggestions.suggestions, only_new: !!onlyNew }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      toast.success(`✅ IA aplicada: ${d.applied} items · ${d.skipped} ya tenían datos humanos`);
      setAiSuggestions(null);
      load();
    } catch (e) { toast.error(e.message || "Error aplicando"); }
    finally { setApplying(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${projectId}/auren`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      setData(j); setDirty({});
    } catch { toast.error("Error cargando checklist"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const updateLocal = (num, patch) => {
    setDirty(prev => {
      const cur = prev[num] || {};
      return { ...prev, [num]: { ...cur, ...patch, item_num: num } };
    });
  };

  const saveAll = async () => {
    const items = Object.values(dirty);
    if (!items.length) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${projectId}/auren/bulk`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ items }),
      });
      if (r.ok) { toast.success(`Guardados ${items.length} cambios`); load(); }
      else toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  const rows = data?.items || [];
  const getRow = (r) => ({ ...r, ...(dirty[r.num] || {}) });

  const visibleRows = bulkFilter === "all" ? rows : rows.filter(r => r.categoria === bulkFilter);
  const selectedNums = Object.keys(selected).filter(k => selected[k]).map(Number);

  const pushSnapshot = (label, actionType) => {
    const ts = new Date().toISOString();
    const snap = { dirty: { ...dirty }, selected: { ...selected }, label, ts };
    setUndoStack(prev => [snap, ...prev].slice(0, 5));
    const logEntry = { label, ts, reverted: false, project_id: projectId, project_name: projectName, action_type: actionType };
    setDayLog(prev => {
      const next = [logEntry, ...prev].slice(0, 200);
      try { localStorage.setItem("pg07_timeline_v1", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const bulkMarkDone = () => {
    pushSnapshot(`Marcar ${selectedNums.length} seleccionados como Done`, "bulk_selected_done");
    const patch = {};
    for (const num of selectedNums) {
      patch[num] = { item_num: num, estado_local: "Done", applies: true, ...(dirty[num] || {}) };
    }
    setDirty(prev => ({ ...prev, ...patch }));
    toast.success(`${selectedNums.length} ítems marcados como Done (pendiente Guardar). Puedes Deshacer.`);
  };

  const bulkMarkAppliesAsDone = () => {
    const count = visibleRows.filter(r => r.applies !== false).length;
    pushSnapshot(`Marcar ${count} aplicables como Done`, "bulk_applies_done");
    const patch = {};
    for (const r of visibleRows) {
      if (r.applies !== false) {
        patch[r.num] = { item_num: r.num, estado_local: "Done", applies: true, ...(dirty[r.num] || {}) };
      }
    }
    setDirty(prev => ({ ...prev, ...patch }));
    toast.success(`${Object.keys(patch).length} ítems marcados como Done (pendiente Guardar). Puedes Deshacer.`);
  };

  const undoAt = (index) => {
    const snap = undoStack[index];
    if (!snap) return;
    setDirty(snap.dirty);
    setSelected(snap.selected);
    setUndoStack(prev => prev.slice(index + 1));
    setDayLog(prev => {
      const next = prev.map((e, i) => i <= index && e.project_id === projectId ? { ...e, reverted: true } : e);
      try { localStorage.setItem("pg07_timeline_v1", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    toast.info(`Revertido: "${snap.label}"`);
  };

  const toggleAll = (checked) => {
    if (checked) {
      const s = {};
      for (const r of visibleRows) s[r.num] = true;
      setSelected(s);
    } else {
      setSelected({});
    }
  };

  if (loading) return <Card><CardContent className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="auren-project-section">
      <CardContent className="p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" />Ficha ISO 56001 por proyecto</h3>
            <p className="text-xs text-muted-foreground">{data.applies_count} de {data.total_items} ítems aplican · {data.summary.pct_done}% completados</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={aiInfer} disabled={aiLoading} variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/30" data-testid="auren-ai-infer-btn" title="Usa Claude para inferir automáticamente qué items aplican y proponer estado inicial">
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Sugerir con IA
            </Button>
            <Button onClick={saveAll} disabled={saving || !Object.keys(dirty).length} data-testid="auren-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              Guardar cambios ({Object.keys(dirty).length})
            </Button>
          </div>
        </div>
        {aiSuggestions && (
          <div className="rounded-lg border-2 border-violet-300 bg-violet-50/60 dark:bg-violet-950/20 p-3 mb-3" data-testid="auren-ai-preview">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold text-violet-800 dark:text-violet-200 flex items-center gap-1"><Sparkles className="w-4 h-4"/>Sugerencias IA — {aiSuggestions.count} items</div>
              <div className="flex gap-1">
                <Button size="sm" onClick={() => applySuggestions(true)} disabled={applying} data-testid="auren-ai-apply-new" className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs">
                  {applying ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : null}
                  Aplicar sólo a items nuevos
                </Button>
                <Button size="sm" variant="outline" onClick={() => applySuggestions(false)} disabled={applying} data-testid="auren-ai-apply-all" className="text-xs">
                  Sobrescribir todo
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAiSuggestions(null)} className="text-xs" data-testid="auren-ai-close">Cerrar</Button>
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto space-y-1 text-xs">
              {aiSuggestions.suggestions.slice(0, 30).map((s, i) => (
                <div key={i} className="flex items-center gap-2 py-1 border-b border-violet-200/60">
                  <code className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 flex-shrink-0">#{s.item_num}</code>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${s.applies ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{s.applies ? s.estado_local : "No aplica"}</span>
                  <span className="flex-1 truncate italic text-violet-800 dark:text-violet-300" title={s.rationale}>{s.rationale}</span>
                </div>
              ))}
              {aiSuggestions.suggestions.length > 30 && <div className="text-[10px] text-muted-foreground text-center pt-1">…y {aiSuggestions.suggestions.length - 30} más</div>}
            </div>
          </div>
        )}
        <div className="grid grid-cols-4 gap-2 mb-3 text-center text-xs">
          <div className="p-2 bg-emerald-50 dark:bg-emerald-950/30 rounded"><div className="font-black text-emerald-700">{data.summary.Done}</div>Done</div>
          <div className="p-2 bg-amber-50 dark:bg-amber-950/30 rounded"><div className="font-black text-amber-700">{data.summary.WIP}</div>WIP</div>
          <div className="p-2 bg-red-50 dark:bg-red-950/30 rounded"><div className="font-black text-red-700">{data.summary.NotS}</div>NotS</div>
          <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded"><div className="font-black text-slate-600">{data.summary.NA}</div>NA</div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-slate-50 dark:bg-slate-800/30 rounded" data-testid="auren-bulk-toolbar">
          <select value={bulkFilter} onChange={e => { setBulkFilter(e.target.value); setSelected({}); }} className="text-xs px-2 py-1 rounded border" data-testid="auren-bulk-filter">
            <option value="all">Todas las categorías</option>
            <option value="Carpeta_Proyecto">Carpeta Proyecto (24)</option>
          </select>
          <Button size="sm" variant="outline" onClick={bulkMarkAppliesAsDone} data-testid="auren-bulk-all-done">
            Marcar aplicables como Done ({visibleRows.filter(r => r.applies !== false).length})
          </Button>
          <Button size="sm" variant="outline" disabled={!selectedNums.length} onClick={bulkMarkDone} data-testid="auren-bulk-selected-done">
            Marcar seleccionados como Done ({selectedNums.length})
          </Button>
          {selectedNums.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setSelected({})} data-testid="auren-bulk-clear">Limpiar selección</Button>
          )}
          {undoStack.length > 0 && (
            <div className="relative group">
              <Button size="sm" variant="outline" className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800" onClick={() => undoAt(0)} data-testid="auren-bulk-undo">
                <Undo2 className="w-3 h-3 mr-1" />Deshacer última ({undoStack.length})
              </Button>
              <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-white dark:bg-slate-900 border rounded shadow-lg p-2 z-10 min-w-[280px]" data-testid="undo-history-panel">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Historial revertible (últimas {undoStack.length})</div>
                {undoStack.map((s, i) => (
                  <button key={s.ts} onClick={() => undoAt(i)} className="w-full text-left flex items-center gap-2 py-1 px-1.5 rounded hover:bg-amber-50 dark:hover:bg-amber-950/30 text-xs" data-testid={`undo-history-${i}`}>
                    <Undo2 className="w-3 h-3 text-amber-600 flex-shrink-0" />
                    <span className="flex-1 truncate">{s.label}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(s.ts).toLocaleTimeString().slice(0, 5)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {dayLog.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setShowTimeline(v => !v)} data-testid="undo-timeline-toggle">
              <History className="w-3 h-3 mr-1" />Timeline ({dayLog.length})
            </Button>
          )}
        </div>

        <div className="max-h-[520px] overflow-auto border rounded">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 z-10">
              <tr className="text-left uppercase tracking-wide text-[10px] text-muted-foreground">
                <th className="px-1 py-2 w-6"><input type="checkbox" checked={selectedNums.length === visibleRows.length && visibleRows.length > 0} onChange={e => toggleAll(e.target.checked)} data-testid="auren-select-all" /></th>
                <th className="px-2 py-2 w-10">#</th>
                <th className="px-2 py-2">Ítem</th>
                <th className="px-2 py-2 w-16">Aplica</th>
                <th className="px-2 py-2 w-24">Estado</th>
                <th className="px-2 py-2 w-40">Responsable</th>
                <th className="px-2 py-2 w-40">Evidencia</th>
                <th className="px-2 py-2 w-40">Notas</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((raw, i) => {
                const r = getRow(raw);
                const isDirty = !!dirty[r.num];
                const meta = estadoMeta[r.estado_efectivo || raw.estado_efectivo] || estadoMeta.NotS;
                return (
                  <tr key={r.num} className={`border-t hover:bg-slate-50 dark:hover:bg-slate-800/40 ${isDirty ? "bg-emerald-50/50 dark:bg-emerald-950/20" : ""}`} data-testid={`auren-row-${i}`}>
                    <td className="px-1 py-1 text-center">
                      <input type="checkbox" checked={!!selected[r.num]} onChange={e => setSelected(prev => ({ ...prev, [r.num]: e.target.checked }))} data-testid={`auren-select-${i}`} />
                    </td>
                    <td className="px-2 py-1 text-muted-foreground">{r.num}</td>
                    <td className="px-2 py-1"><div className="truncate max-w-md">{raw.name}</div>{raw.categoria && <div className="text-[9px] text-muted-foreground">{raw.categoria}</div>}</td>
                    <td className="px-2 py-1">
                      <input type="checkbox" checked={r.applies !== false} onChange={e => updateLocal(r.num, { applies: e.target.checked })} data-testid={`auren-applies-${i}`} />
                    </td>
                    <td className="px-2 py-1">
                      <select
                        value={r.estado_local || ""}
                        onChange={e => updateLocal(r.num, { estado_local: e.target.value || null })}
                        className={`text-[11px] px-1.5 py-0.5 rounded border ${meta.cls}`}
                        data-testid={`auren-estado-${i}`}
                      >
                        <option value="">(global: {raw.estado_efectivo})</option>
                        <option value="Done">Done</option>
                        <option value="WIP">WIP</option>
                        <option value="NotS">NotS</option>
                        <option value="NA">NA</option>
                      </select>
                    </td>
                    <td className="px-2 py-1"><Input className="h-7 text-xs" value={r.responsable ?? raw.responsable_global ?? ""} onChange={e => updateLocal(r.num, { responsable: e.target.value })} placeholder={raw.responsable_global || "—"} data-testid={`auren-resp-${i}`} /></td>
                    <td className="px-2 py-1"><Input className="h-7 text-xs" value={r.evidencia ?? ""} onChange={e => updateLocal(r.num, { evidencia: e.target.value })} placeholder="URL / doc" data-testid={`auren-evid-${i}`} /></td>
                    <td className="px-2 py-1"><Input className="h-7 text-xs" value={r.notas ?? ""} onChange={e => updateLocal(r.num, { notas: e.target.value })} data-testid={`auren-notas-${i}`} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>

      {showTimeline && (() => {
        const uniqueProjects = Array.from(new Map(dayLog.map(e => [e.project_id, e.project_name || e.project_id])).entries());
        const uniqueActions = Array.from(new Set(dayLog.map(e => e.action_type).filter(Boolean)));
        const actionLabels = { bulk_selected_done: "Seleccionados → Done", bulk_applies_done: "Aplicables → Done" };
        const filtered = dayLog.filter(e => {
          if (timelineFilters.project && e.project_id !== timelineFilters.project) return false;
          if (timelineFilters.action && e.action_type !== timelineFilters.action) return false;
          if (timelineFilters.onlyReverted && !e.reverted) return false;
          return true;
        });
        const totalRevertedShown = filtered.filter(e => e.reverted).length;
        return (
          <div className="fixed right-4 top-20 bottom-4 w-96 bg-white dark:bg-slate-900 border rounded-lg shadow-2xl z-40 flex flex-col" data-testid="undo-timeline-panel">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-emerald-600" />
                <div>
                  <div className="text-sm font-bold">Timeline</div>
                  <div className="text-[10px] text-muted-foreground">{filtered.length}/{dayLog.length} acciones · {totalRevertedShown} revertidas</div>
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setShowTimeline(false)} data-testid="undo-timeline-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="px-3 py-2 border-b space-y-1.5 bg-slate-50/50 dark:bg-slate-800/30" data-testid="timeline-filters">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-14">Proyecto</span>
                <select value={timelineFilters.project} onChange={e => setTimelineFilters(f => ({ ...f, project: e.target.value }))} className="flex-1 text-xs h-7 rounded border px-1.5 bg-transparent" data-testid="timeline-filter-project">
                  <option value="">Todos ({uniqueProjects.length})</option>
                  {uniqueProjects.map(([pid, pname]) => <option key={pid} value={pid}>{pname}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-14">Acción</span>
                <select value={timelineFilters.action} onChange={e => setTimelineFilters(f => ({ ...f, action: e.target.value }))} className="flex-1 text-xs h-7 rounded border px-1.5 bg-transparent" data-testid="timeline-filter-action">
                  <option value="">Todas</option>
                  {uniqueActions.map(a => <option key={a} value={a}>{actionLabels[a] || a}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="timeline-filter-reverted-label">
                <input type="checkbox" checked={timelineFilters.onlyReverted} onChange={e => setTimelineFilters(f => ({ ...f, onlyReverted: e.target.checked }))} data-testid="timeline-filter-reverted" />
                Solo revertidas
              </label>
              {(timelineFilters.project || timelineFilters.action || timelineFilters.onlyReverted) && (
                <button className="text-[10px] text-violet-700 hover:underline" onClick={() => setTimelineFilters({ project: "", action: "", onlyReverted: false })} data-testid="timeline-filter-clear">Limpiar filtros</button>
              )}
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-2">
              {filtered.map((e, i) => (
                <div key={e.ts} className={`border-l-4 pl-3 py-1.5 ${e.reverted ? "border-slate-300 opacity-50" : "border-emerald-500"}`} data-testid={`timeline-entry-${i}`}>
                  <div className="text-xs font-medium">
                    {e.reverted && <span className="line-through">{e.label}</span>}
                    {!e.reverted && e.label}
                  </div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
                    <span>{new Date(e.ts).toLocaleTimeString()}</span>
                    {e.project_name && <span className="truncate max-w-[160px]" title={e.project_name}>· {e.project_name}</span>}
                    {e.reverted && <Badge variant="outline" className="text-[9px] h-4 px-1">Revertida</Badge>}
                  </div>
                </div>
              ))}
              {!filtered.length && <div className="text-xs text-muted-foreground italic">Sin acciones que coincidan con los filtros.</div>}
            </div>
          </div>
        );
      })()}
    </Card>
  );
}

function CierreSection({ projectId, token }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await fetch(`${API}/pg07/projects/${projectId}/cierre`, { headers: { Authorization: `Bearer ${token}` } });
    const j = await r.json();
    setForm({
      propuesta_presentada: j.propuesta_presentada || false,
      propuesta_fecha: j.propuesta_fecha || "",
      propuesta_doc_url: j.propuesta_doc_url || "",
      lecciones_aprendidas: j.lecciones_aprendidas || "",
      resultados: j.resultados || "",
      proximos_pasos: j.proximos_pasos || "",
      pct_override: j.pct_override ?? "",
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, pct_override: form.pct_override === "" ? null : Number(form.pct_override) };
      const r = await fetch(`${API}/pg07/projects/${projectId}/cierre`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (r.ok) toast.success("Ficha de cierre guardada");
      else toast.error("Error");
    } finally { setSaving(false); }
  };

  if (!form) return null;
  return (
    <Card data-testid="cierre-section">
      <CardContent className="p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Award className="w-5 h-5 text-amber-600" />Ficha de Cierre</h3>
            <p className="text-xs text-muted-foreground">Propuesta presentada + lecciones aprendidas + resultados.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} data-testid="cierre-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              Guardar
            </Button>
            <Button variant="outline" onClick={async () => {
              const r = await fetch(`${API}/pg07/projects/${projectId}/cierre/pdf`, { headers: { Authorization: `Bearer ${token}` } });
              if (!r.ok) { toast.error("Error generando PDF"); return; }
              const blob = await r.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = `PG07_cierre_${projectId.slice(0,8)}.pdf`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
            }} data-testid="cierre-pdf-btn">
              <FileDown className="w-3 h-3 mr-1" />PDF
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-xs">Propuesta presentada</Label>
            <div className="flex items-center h-9">
              <input type="checkbox" id={`p-${projectId}`} checked={!!form.propuesta_presentada} onChange={e => setForm({ ...form, propuesta_presentada: e.target.checked })} data-testid="cierre-propuesta-check" />
              <label htmlFor={`p-${projectId}`} className="ml-2 text-sm">Sí, se presentó</label>
            </div>
          </div>
          <div><Label className="text-xs">Fecha</Label><Input type="date" value={form.propuesta_fecha} onChange={e => setForm({ ...form, propuesta_fecha: e.target.value })} data-testid="cierre-fecha" /></div>
          <div><Label className="text-xs">Enlace al documento</Label><Input value={form.propuesta_doc_url} onChange={e => setForm({ ...form, propuesta_doc_url: e.target.value })} placeholder="https://..." data-testid="cierre-doc-url" /></div>
        </div>

        <div className="space-y-3">
          <div><Label className="text-xs">Resultados obtenidos</Label><Textarea rows={3} value={form.resultados} onChange={e => setForm({ ...form, resultados: e.target.value })} placeholder="¿Qué se logró? KPIs, resultados clave..." data-testid="cierre-resultados" /></div>
          <div><Label className="text-xs">Lecciones aprendidas</Label><Textarea rows={3} value={form.lecciones_aprendidas} onChange={e => setForm({ ...form, lecciones_aprendidas: e.target.value })} placeholder="Qué salió bien / mal, aprendizajes accionables..." data-testid="cierre-lecciones" /></div>
          <div><Label className="text-xs">Próximos pasos</Label><Textarea rows={2} value={form.proximos_pasos} onChange={e => setForm({ ...form, proximos_pasos: e.target.value })} placeholder="Recomendaciones, follow-ups, escalados..." data-testid="cierre-next" /></div>
          <div className="max-w-xs">
            <Label className="text-xs">% cierre manual (override)</Label>
            <Input type="number" min="0" max="100" value={form.pct_override} onChange={e => setForm({ ...form, pct_override: e.target.value })} placeholder="Déjalo vacío para calcular automático (4 casillas)" data-testid="cierre-pct-override" />
            <p className="text-[10px] text-muted-foreground mt-1">Solo si el % real difiere del automático (ej. pendiente de resolución externa como CDTI).</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AuditoriaSection({ projectId, token }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const currentYear = new Date().getFullYear();

  const load = async () => {
    const r = await fetch(`${API}/pg07/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      const p = await r.json();
      setForm({
        auditoria_status: p.auditoria_status || "",
        auditoria_fecha: p.auditoria_fecha || "",
        auditoria_year: p.auditoria_year || currentYear,
        auditoria_notes: p.auditoria_notes || "",
      });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, auditoria_status: form.auditoria_status || null };
      const r = await fetch(`${API}/pg07/projects/${projectId}/auditoria`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (r.ok) toast.success("Auditoría guardada");
      else toast.error("Error");
    } finally { setSaving(false); }
  };

  if (!form) return null;
  const statusColors = {
    candidato: "bg-amber-100 text-amber-800 border-amber-300",
    seleccionado: "bg-emerald-100 text-emerald-800 border-emerald-300",
    descartado: "bg-slate-100 text-slate-600 border-slate-300",
  };

  return (
    <Card data-testid="auditoria-section">
      <CardContent className="p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Scale className="w-5 h-5 text-violet-600" />Auditoría anual · Candidato / Seleccionado</h3>
            <p className="text-xs text-muted-foreground">Marca si este proyecto participa en la auditoría anual de innovación (ISO 56001).</p>
          </div>
          <div className="flex items-center gap-2">
            {form.auditoria_status && (
              <Badge className={`text-xs ${statusColors[form.auditoria_status] || ""}`} data-testid="auditoria-current-badge">
                {form.auditoria_status.toUpperCase()}
              </Badge>
            )}
            <Button onClick={save} disabled={saving} data-testid="auditoria-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              Guardar
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3 mb-3">
          <div>
            <Label className="text-xs">Estado</Label>
            <select
              value={form.auditoria_status || ""}
              onChange={e => setForm({ ...form, auditoria_status: e.target.value })}
              className="w-full h-9 rounded border px-2 text-sm bg-transparent"
              data-testid="auditoria-status-select"
            >
              <option value="">(sin estado)</option>
              <option value="candidato">Candidato</option>
              <option value="seleccionado">Seleccionado</option>
              <option value="descartado">Descartado</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Año</Label>
            <Input type="number" value={form.auditoria_year || ""} onChange={e => setForm({ ...form, auditoria_year: parseInt(e.target.value || currentYear) })} data-testid="auditoria-year" />
          </div>
          <div>
            <Label className="text-xs">Fecha estado / decisión</Label>
            <Input type="date" value={form.auditoria_fecha || ""} onChange={e => setForm({ ...form, auditoria_fecha: e.target.value })} data-testid="auditoria-fecha" />
          </div>
        </div>

        <div>
          <Label className="text-xs">Notas (motivo de selección, observaciones del auditor, etc.)</Label>
          <Textarea rows={2} value={form.auditoria_notes || ""} onChange={e => setForm({ ...form, auditoria_notes: e.target.value })} data-testid="auditoria-notes" />
        </div>
      </CardContent>
    </Card>
  );
}

